import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Plus, Presentation, Wand2, Download, Bot, BarChart3, Printer } from "lucide-react";
import { Shell, Stat, Modal } from "../components/Shell";
import { SpecPicker, SheetView, specTotal, toastSpecError } from "../components/Worksheet";
import { ClassReport } from "../components/ClassReport";
import { useLang } from "../i18n";
import { api, API } from "../api";
import { ASSETS } from "../assets";

export default function Teacher() {
  const { t } = useLang();
  const [tab, setTab] = useState("classes");
  const [classes, setClasses] = useState([]);
  const [topics, setTopics] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [subs, setSubs] = useState([]);
  const [report, setReport] = useState({ rows: [] });
  const [modal, setModal] = useState(null);
  const [target, setTarget] = useState(null);
  const [ws, setWs] = useState(null);
  const [busy, setBusy] = useState(false);
  const [aiTip, setAiTip] = useState(null);
  const [wsTopic, setWsTopic] = useState("");
  const [wsGrade, setWsGrade] = useState(5);
  const [showAnswers, setShowAnswers] = useState(true);
  const [spec, setSpec] = useState({ mcq: 4, true_false: 3, fill_blank: 2, find_mistake: 2, open: 2, match: 0 });
  const [taskSpec, setTaskSpec] = useState({ mcq: 3, true_false: 2, fill_blank: 2, open: 1, find_mistake: 0, match: 0 });
  const [classReport, setClassReport] = useState(null);

  const load = async () => {
    const [c, a, s, r] = await Promise.all([
      api.get("/classes"), api.get("/assignments"), api.get("/submissions"), api.get("/teacher/reports"),
    ]);
    setClasses(c.data); setAssignments(a.data); setSubs(s.data); setReport(r.data);
    const grades = [...new Set(c.data.map((x) => x.grade))];
    const tps = await Promise.all(grades.map((g) => api.get(`/topics?grade=${g}`)));
    setTopics(tps.flatMap((x) => x.data));
  };
  useEffect(() => { load().catch(() => toast.error("Load failed")); }, []);

  const nav = [{ key: "dashboard", to: "/teacher", label: t("myClasses"), icon: ASSETS.topics }];

  const createAssignment = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    await api.post("/assignments", {
      class_id: f.get("class_id"), title: f.get("title"),
      instructions: f.get("instructions"), kind: f.get("kind"), due_date: f.get("due_date"),
    });
    setModal(null); toast.success("تم إنشاء المهمة"); load();
  };

  const gradeSub = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    await api.post(`/submissions/${target.id}/grade`, {
      score: Number(f.get("score")), feedback: f.get("feedback"),
    });
    setModal(null); setAiTip(null); toast.success("تم التصحيح"); load();
  };

  const aiGrade = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/submissions/${target.id}/ai-grade`);
      setAiTip(data);
      toast.success("اقتراح الذكاء الاصطناعي جاهز (إرشادي)");
    } catch { toast.error("AI grading failed"); } finally { setBusy(false); }
  };

  const genWorksheet = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    setBusy(true);
    try {
      const { data } = await api.post("/ai/worksheet", { topic: f.get("topic"), grade: Number(f.get("grade")) });
      setWs(data); toast.success("تم توليد ورقة العمل");
    } catch { toast.error("AI failed"); } finally { setBusy(false); }
  };

  const buildWorksheet = async () => {
    if (!wsTopic.trim()) return toast.error("اكتب موضوع الورقة");
    if (specTotal(spec) === 0) return toastSpecError();
    setBusy(true);
    try {
      const { data } = await api.post("/ai/worksheet/build", {
        topic: wsTopic, grade: wsGrade, spec, show_answers: showAnswers,
      });
      setWs(data); toast.success("تم بناء ورقة العمل");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر توليد الورقة");
    } finally { setBusy(false); }
  };

  const createSheetTask = async (e) => {
    e.preventDefault();
    if (specTotal(taskSpec) === 0) return toastSpecError();
    const f = new FormData(e.target);
    setBusy(true);
    try {
      await api.post("/assignments/sheet", {
        class_id: f.get("class_id"), title: f.get("title"), topic: f.get("topic"),
        spec: taskSpec, due_date: f.get("due_date"),
      });
      setModal(null); toast.success("تم إنشاء المهمة وإرسالها للفصل"); load();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "تعذّر إنشاء المهمة");
    } finally { setBusy(false); }
  };

  const openClassReport = async (classId) => {
    setModal("report");
    setClassReport(null);
    try {
      const { data } = await api.get(`/classes/${classId}/report`);
      setClassReport(data);
    } catch { toast.error("تعذّر تحميل التقرير"); }
  };

  const downloadPdf = async (withAnswers = false) => {
    const res = await api.get(`/ai/worksheet/${ws.id}/pdf?answers=${withAnswers ? 1 : 0}`,
      { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = withAnswers ? "worksheet-answers.pdf" : "worksheet.pdf"; a.click();
  };

  const tabs = [["classes", t("myClasses")], ["assignments", t("assignments")],
    ["inbox", t("inbox")], ["worksheets", t("worksheets")], ["reports", t("reports")]];

  return (
    <Shell nav={nav}>
      <h1 className="text-3xl sm:text-4xl font-black text-white glow-text">{t("teacher")}</h1>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <Stat label={t("myClasses")} value={classes.length} testid="stat-classes" />
        <Stat label={t("students")} value={report.rows.length} testid="stat-students" />
        <Stat label={t("assignments")} value={assignments.length} testid="stat-assignments" />
        <Stat label={t("inbox")} value={subs.filter((s) => s.score === null).length}
          color="var(--play-accent)" testid="stat-inbox" />
      </div>

      <div className="flex gap-2 mt-10 flex-wrap">
        {tabs.map(([k, l]) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
            className={`pro-btn text-sm ${tab === k ? "text-white" : "bg-white/12 text-white border border-white/20"}`}
            style={tab === k ? { background: "linear-gradient(180deg,#ffb43f,#ef8a00)", color: "#05122e" } : {}}>{l}</button>
        ))}
      </div>

      <div className="mt-4">
        {tab === "classes" && (
          <div className="grid md:grid-cols-2 gap-6">
            {classes.map((c) => (
              <div key={c.id} className="pro-card" data-testid={`class-card-${c.id}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-xl font-bold">{c.name}</h3>
                    <p className="text-sm text-white/60">{t("grade")} {c.grade} · {c.student_ids.length} {t("students")}</p>
                  </div>
                </div>
                <div className="mt-4 space-y-2">
                  <div className="text-xs uppercase font-bold tracking-widest text-white/45">{t("topics")}</div>
                  {topics.filter((tp) => tp.grade === c.grade).map((tp) => (
                    <Link key={tp.id} to={`/teach/${tp.id}`} data-testid={`teach-topic-${tp.id}`}
                      className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 hover:bg-slate-50">
                      <span className="text-sm font-semibold">{tp.title_en} · {tp.title_ar}</span>
                      <span className="text-xs font-bold flex items-center gap-1" style={{ color: "var(--pro-secondary)" }}>
                        <Presentation size={14} />{t("teachingMode")}</span>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
            {!classes.length && <div className="pro-card text-white/45">{t("noData")}</div>}
          </div>
        )}

        {tab === "assignments" && (
          <div className="pro-card">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">{t("assignments")}</h2>
              <button data-testid="add-assignment-btn" onClick={() => setModal("assignment")}
                className="pro-btn text-white flex items-center gap-1" style={{ background: "linear-gradient(180deg,#3fa9ff,#1e63c8)" }}>
                <Plus size={16} />{t("newAssignment")}</button>
              <button data-testid="add-sheet-task-btn" onClick={() => setModal("sheet-task")}
                className="pro-btn flex items-center gap-1"
                style={{ background: "linear-gradient(180deg,#ffb43f,#ef8a00)", color: "#05122e" }}>
                <Wand2 size={16} />مهمة بأنواع أسئلة</button>
            </div>
            <table className="pro" data-testid="assignments-table">
              <thead><tr><th>{t("title")}</th><th>{t("classes")}</th><th>Type</th><th>{t("submitted")}</th></tr></thead>
              <tbody>
                {assignments.map((a) => (
                  <tr key={a.id} data-testid={`assignment-row-${a.id}`}>
                    <td className="font-bold">{a.title}</td><td>{a.class_name}</td>
                    <td>{a.kind}</td><td>{a.submissions}</td>
                  </tr>
                ))}
                {!assignments.length && <tr><td colSpan="4" className="text-white/45">{t("noData")}</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "inbox" && (
          <div className="pro-card">
            <h2 className="text-xl font-bold mb-4">{t("inbox")}</h2>
            <table className="pro" data-testid="inbox-table">
              <thead><tr><th>{t("students")}</th><th>{t("title")}</th><th>{t("score")}</th><th /></tr></thead>
              <tbody>
                {subs.map((s) => (
                  <tr key={s.id} data-testid={`submission-row-${s.id}`}>
                    <td className="font-bold">{s.student_name}</td>
                    <td>{s.assignment_title}</td>
                    <td>{s.score === null ? <span className="text-amber-600 font-bold">{t("pending")}</span> : `${s.score}%`}</td>
                    <td><button data-testid={`grade-btn-${s.id}`}
                      onClick={() => { setTarget(s); setAiTip(null); setModal("grade"); }}
                      className="pro-btn bg-white/12 text-white border border-white/20 text-xs">{t("grading")}</button></td>
                  </tr>
                ))}
                {!subs.length && <tr><td colSpan="4" className="text-white/45">{t("noData")}</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "worksheets" && (
          <div className="pro-card">
            <h2 className="text-xl font-bold mb-1">{t("worksheets")}</h2>
            <p className="text-sm text-white/60 mb-4">اختر الموضوع والصف، ثم حدّد عدد الأسئلة لكل نوع.</p>
            <div className="flex flex-wrap gap-3 items-end">
              <input value={wsTopic} onChange={(e) => setWsTopic(e.target.value)}
                className="input max-w-xs" placeholder="Topic (مثال: Unit 2 - Sports)"
                data-testid="ws-topic-input" />
              <select value={wsGrade} onChange={(e) => setWsGrade(Number(e.target.value))}
                className="input max-w-[120px]" data-testid="ws-grade-select">
                {[3, 4, 5, 6, 7, 8, 9].map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
              <label className="flex items-center gap-2 text-xs text-white/70">
                <input type="checkbox" checked={showAnswers} data-testid="ws-answers-toggle"
                  onChange={(e) => setShowAnswers(e.target.checked)} />نموذج الإجابة
              </label>
            </div>
            <div className="mt-4"><SpecPicker value={spec} onChange={setSpec} testidPrefix="ws-spec" /></div>
            <button onClick={buildWorksheet} disabled={busy} data-testid="ws-generate-btn"
              className="pro-btn text-white flex items-center gap-2 mt-4 disabled:opacity-60"
              style={{ background: "linear-gradient(180deg,#3fa9ff,#1e63c8)" }}>
              <Wand2 size={16} />{busy ? t("loading") : t("generateWorksheet")}</button>

            {ws && (
              <div className="mt-6 border-t border-white/10 pt-4" data-testid="worksheet-preview">
                <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
                  <div className="flex gap-2 ms-auto">
                    <button onClick={() => downloadPdf(false)} data-testid="ws-download-btn"
                      className="pro-btn text-white text-sm flex items-center gap-2"
                      style={{ background: "linear-gradient(180deg,#3fa9ff,#1e63c8)" }}>
                      <Download size={16} />ورقة الطالب</button>
                    {showAnswers && (
                      <button onClick={() => downloadPdf(true)} data-testid="ws-answers-download-btn"
                        className="pro-btn bg-white/12 text-white border border-white/20 text-sm flex items-center gap-2">
                        <Download size={16} />نموذج الإجابة</button>
                    )}
                  </div>
                </div>
                <SheetView sheet={ws} showAnswers={showAnswers} />
              </div>
            )}
          </div>
        )}

        {tab === "reports" && (
          <div className="space-y-4">
            <div className="pro-card">
              <h2 className="text-xl font-bold mb-3">تقارير الفصول</h2>
              <div className="flex flex-wrap gap-2">
                {classes.map((c) => (
                  <button key={c.id} onClick={() => openClassReport(c.id)}
                    data-testid={`class-report-btn-${c.id}`}
                    className="pro-btn bg-white/12 text-white border border-white/20 text-sm flex items-center gap-2">
                    <BarChart3 size={15} />{c.name} · {t("grade")} {c.grade}</button>
                ))}
                {!classes.length && <span className="text-white/45 text-sm">{t("noData")}</span>}
              </div>
            </div>
            <div className="pro-card overflow-x-auto">
              <h2 className="text-xl font-bold mb-4">{t("reports")} — كل الطلاب</h2>
              <table className="pro" data-testid="reports-table">
                <thead><tr><th>{t("name")}</th><th>{t("grade")}</th><th>{t("points")}</th>
                  <th>{t("quizzesTaken")}</th><th>{t("avgScore")}</th><th>{t("submitted")}</th></tr></thead>
                <tbody>
                  {report.rows.map((r) => (
                    <tr key={r.id} data-testid={`report-row-${r.id}`}>
                      <td className="font-bold">{r.name}</td><td>{r.grade}</td><td>{r.points}</td>
                      <td>{r.quizzes}</td><td>{r.avg_score}%</td><td>{r.submissions}</td>
                    </tr>
                  ))}
                  {!report.rows.length && <tr><td colSpan="6" className="text-white/45">{t("noData")}</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <Modal open={modal === "assignment"} onClose={() => setModal(null)} title={t("newAssignment")} testid="assignment-modal">
        <form onSubmit={createAssignment} className="space-y-3">
          <select name="class_id" className="input" required data-testid="assignment-class-select">
            {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input name="title" className="input" placeholder={t("title")} required data-testid="assignment-title-input" />
          <textarea name="instructions" rows="4" className="input" placeholder={t("instructions")} required
            data-testid="assignment-instructions-input" />
          <select name="kind" className="input" data-testid="assignment-kind-select">
            <option value="writing">Writing</option>
            <option value="reading">Reading</option>
            <option value="open_question">Open question</option>
          </select>
          <input name="due_date" type="date" className="input" data-testid="assignment-due-input" />
          <button className="pro-btn text-white w-full" style={{ background: "linear-gradient(180deg,#3fa9ff,#1e63c8)" }}
            data-testid="assignment-save-btn">{t("save")}</button>
        </form>
      </Modal>

      <Modal open={modal === "sheet-task"} onClose={() => setModal(null)}
        title="مهمة بأنواع أسئلة" testid="sheet-task-modal">
        <form onSubmit={createSheetTask} className="space-y-3">
          <select name="class_id" className="input" required data-testid="sheet-class-select">
            {classes.map((c) => <option key={c.id} value={c.id}>{c.name} · الصف {c.grade}</option>)}
          </select>
          <input name="title" className="input" placeholder="عنوان المهمة" required
            data-testid="sheet-title-input" />
          <input name="topic" className="input" placeholder="الموضوع (Topic)" required
            data-testid="sheet-topic-input" dir="ltr" />
          <SpecPicker value={taskSpec} onChange={setTaskSpec} testidPrefix="task-spec" />
          <input name="due_date" type="date" className="input" data-testid="sheet-due-input" />
          <p className="text-xs text-white/50">
            الاختيارات وصح/خطأ وإكمال الفراغ تُصحّح تلقائياً، والأسئلة المفتوحة وتصحيح الخطأ تصلك للتصحيح.
          </p>
          <button disabled={busy} className="pro-btn text-white w-full disabled:opacity-60"
            style={{ background: "linear-gradient(180deg,#3fa9ff,#1e63c8)" }}
            data-testid="sheet-save-btn">{busy ? t("loading") : "إنشاء المهمة"}</button>
        </form>
      </Modal>

      <Modal open={modal === "report"} onClose={() => setModal(null)}
        title="تقرير الفصل" testid="class-report-modal">
        <ClassReport report={classReport} />
      </Modal>

      <Modal open={modal === "grade"} onClose={() => setModal(null)}
        title={`${t("grading")} — ${target?.student_name || ""}`} testid="grade-modal">
        <div className="rounded-xl bg-white/10 border border-white/15 p-3 text-sm mb-4 text-white/85"
          data-testid="submission-answer">{target?.answer}</div>
        <button onClick={aiGrade} disabled={busy} data-testid="ai-grade-btn"
          className="btn3d w-full flex items-center justify-center gap-2 mb-3 text-sm" style={{ background: "linear-gradient(180deg,#8b5cf6,#5b21b6)" }}>
          <Bot size={16} />{busy ? t("loading") : t("aiGrade")}</button>
        {aiTip && (
          <div className="rounded-lg p-3 mb-3 text-sm" style={{ background: "rgba(122,92,240,.25)" }} data-testid="ai-grade-result">
            <b>AI: {aiTip.score}%</b> — {aiTip.feedback_ar}
            <ul className="list-disc ps-5 mt-1">{(aiTip.corrections || []).map((c, i) => <li key={i}>{c}</li>)}</ul>
            <p className="text-xs text-white/60 mt-1">التصحيح الآلي إرشادي فقط</p>
          </div>
        )}
        <form onSubmit={gradeSub} className="space-y-3">
          <input name="score" type="number" min="0" max="100" className="input" placeholder={t("score")}
            defaultValue={aiTip?.score ?? ""} required data-testid="grade-score-input" />
          <textarea name="feedback" rows="3" className="input" placeholder={t("feedback")}
            defaultValue={aiTip?.feedback_ar ?? ""} data-testid="grade-feedback-input" />
          <button className="pro-btn text-white w-full" style={{ background: "linear-gradient(180deg,#3fa9ff,#1e63c8)" }}
            data-testid="grade-save-btn">{t("save")}</button>
        </form>
      </Modal>
    </Shell>
  );
}
